import { useState, useEffect } from 'react'
import { useCustomNodeData } from '@/entities/node'
import type { BaseNodeData } from '@/shared/lib/node-registry.ts'
import aiLoading from '@/assets/images/ic_ai_loading.apng'
import aiFailImg from '@/assets/images/ic_ai_error.svg'
import { useNodeId } from '@xyflow/react'
import { X } from 'lucide-react'
// FSD exception: cancelNodeTask is used to unify cancel flow between node overlay and task panel.
import { cancelNodeTask } from '@/entities/task'
import { ConfirmDialog } from '@/shared/components'
import { useProjectId } from '@/entities/project'
import { useTaskInfoSnapshot } from '@/shared/lib/task/task-info-store'
import { useNodeAsset } from '@/entities/node/canvas-asset'
import { GenerationFailedMessage } from './GenerationFailedMessage'
// FSD exception: failed overlay is an entity-level status UI, but retry must reuse the unified OP submit path.
import { handleSubmitError, submitCanvasNodeTask } from '@/features/op/model/use-submit-op-task'

function renderLoadingIcon() {
  return (
    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-transparent shadow-xl">
      <img alt="空状态插图" src={aiLoading} />
    </div>
  )
}

function renderFailedOverlay(runId: string | undefined, _code: number | undefined, error: string, onRetry: () => void, onClose: () => void, _onErrorClick: () => void, hasThumb: boolean) {
  return (
    <>
      <button
        type="button"
        onClick={onClose}
        className="absolute right-2 top-2 rounded-full p-1.5 text-white/50 transition-colors hover:bg-white/10 hover:text-white"
      >
        <X className="h-4 w-4" />
      </button>
      <div
        className={`h-12 w-12 ${hasThumb ? 'bg-white opacity-75' : 'bg-[#C3C3C3]'}`}
        style={{
          maskImage: `url(${aiFailImg})`,
          WebkitMaskImage: `url(${aiFailImg})`,
          maskSize: 'contain',
          WebkitMaskSize: 'contain',
          maskRepeat: 'no-repeat',
          WebkitMaskRepeat: 'no-repeat',
          maskPosition: 'center',
          WebkitMaskPosition: 'center',
        }}
      />
      <div className="mt-2 flex px2 flex-col items-center">
        <GenerationFailedMessage
          message={error || undefined}
          runId={runId}
          lines={3}
          className="max-w-full text-center"
          labelClassName={`text-[14px] ${hasThumb ? 'text-white/75' : 'text-[#C3C3C3]'}`}
          messageClassName={`text-[11px] ${hasThumb ? 'text-white/65' : 'text-[#C3C3C3]/80'}`}
        />
        {/* {!_code ? null : (
            <button className='text-accent mt-1 p-1' onClick={_onErrorClick}>{ _code == -10 ? '算力不足' : '升级会员' }</button>
          )} */}
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="mt-3 rounded-full bg-white/10 px-4 py-1 text-[12px] text-[#D0D0D0] transition hover:bg-white/20"
      >
        重新生成
      </button>
    </>
  )
}

function renderRunningOverlay(snapshot: BaseNodeData, canCancel: boolean, onRequestCancel: () => void) {
  const progress = snapshot.progress || 0
  const isCreating = snapshot.status === 'running';

  const label = `AI 生成中... ${isCreating && progress > 0 ? ` ${progress}%` : ''}`;

  return (
    <>
      {renderLoadingIcon()}
      <span className="mt-2 text-[12px] font-medium text-[#D0D0D0] drop-shadow-md">
        {label}
      </span>
      {canCancel && (
        <button
          type="button"
          onClick={onRequestCancel}
          className="mt-3 rounded-full bg-white/10 px-4 py-1 text-[12px] text-[#D0D0D0] transition hover:bg-white/20"
        >
          取消
        </button>
      )}
    </>
  )
}

function renderOverlayContent(
  snapshot: BaseNodeData,
  canCancel: boolean,
  onRetry: () => void,
  onClose: () => void,
  onRequestCancel: () => void,
  hasThumb: boolean
) {
  switch (snapshot.status) {
    case 'failed': {
      const code = snapshot.code!
      const msg = snapshot.error || '';
      return renderFailedOverlay(snapshot.runId, code, msg, onRetry, onClose, () => {
        handleSubmitError({ code, msg })
      }, hasThumb)
    }
    case 'pending':
    case 'running':
      return renderRunningOverlay(snapshot, canCancel, onRequestCancel)
    case 'canceled':
    default:
      return null
  }
}

interface AiGenerationOverlayProps {
  hideRunning?: boolean
}

export function AiGenerationOverlay({ hideRunning }: AiGenerationOverlayProps = {}) {
  const nodeId = useNodeId()
  const projectId = String(useProjectId() ?? '')
  const { snapshot } = useCustomNodeData<BaseNodeData>()
  const { taskInfo, runMap } = useTaskInfoSnapshot()
  const { asset } = useNodeAsset(nodeId || undefined)
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false)

  const hasThumb = Boolean(asset?.previewUrl || asset?.url)

  const runId = snapshot.runId || ''
  const status = runId ? snapshot.status : undefined

  useEffect(() => {
    if (status !== 'pending' && status !== 'running') {
      setCancelConfirmOpen(false)
    }
  }, [status])
  const isRunningFlowTaskNode = Boolean(
    nodeId &&
    runId &&
    taskInfo.state === 0 &&
    taskInfo.runId === runId &&
    runMap?.[nodeId] === 0,
  )
  const canCancel = !isRunningFlowTaskNode

  const retryAITask = async () => {
    const normalizedNodeId = nodeId?.trim() || ''
    const normalizedProjectId = projectId.trim()
    if (!normalizedNodeId || !normalizedProjectId) return

    await submitCanvasNodeTask({
      projectId: normalizedProjectId,
      nodeId: normalizedNodeId,
      source: 'failed-overlay-retry',
    })
  }

  if (!nodeId || !status || status === 'completed') {
    return null
  }

  if (hideRunning && (status === 'running')) {
    return null
  }

  return (
    <div className="absolute inset-[-1.5px] z-100 flex flex-col items-center justify-center rounded-2xl bg-black/80 backdrop-blur-xs">
      {renderOverlayContent(snapshot, canCancel, () => {
        void retryAITask()
      }, () => {
        void cancelNodeTask(nodeId);
      }, () => {
        setCancelConfirmOpen(true)
      }, hasThumb)}

      <ConfirmDialog
        open={cancelConfirmOpen}
        onOpenChange={setCancelConfirmOpen}
        title="取消任务"
        description={"任务正在生成中，确定要取消该任务吗？\n请注意，取消操作不会返还算力"}
        confirmTone="danger"
        onConfirm={() => {
          void cancelNodeTask(nodeId)
        }}
      />
    </div>
  )
}
